import { useMemo } from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import {
	SCORE_FIELD_CANDIDATES,
	extractScore as extractScoreFromData,
} from "../../utils/extractScore";

interface Props {
	formConfig?: Record<string, unknown>;
	responses?: Record<string, unknown>;
	mode?: "view" | "review";
}

interface SurveyResult {
	model: Model | null;
	error?: string;
}

const extractScore = (
	model: Model | null,
	responses: Record<string, unknown> | undefined,
): number | undefined => {
	// Prefer the live model value (it reflects any computed score), then fall
	// back to the shared submittedData lookup so both readers use the same keys.
	const fromModel = model
		? SCORE_FIELD_CANDIDATES.map((key) => model.getValue(key)).find(
				(v) => typeof v === "number",
			)
		: undefined;
	if (typeof fromModel === "number") return fromModel;

	return extractScoreFromData(responses);
};

const buildModel = (
	formConfig: Record<string, unknown> | undefined,
	responses: Record<string, unknown> | undefined,
): SurveyResult => {
	if (!formConfig) return { model: null };
	const schema =
		(formConfig.surveySchema as Record<string, unknown> | undefined) ??
		formConfig;
	if (!schema) return { model: null };

	try {
		const model = new Model(schema);
		model.mode = "display";
		model.showCompletedPage = false;
		model.showNavigationButtons = false;
		if (responses) {
			model.data = responses;
		}
		return { model };
	} catch (err) {
		return {
			model: null,
			error: err instanceof Error ? err.message : "Invalid survey schema",
		};
	}
};

export const FormDisplay = ({ formConfig, responses }: Props) => {
	const { model, error } = useMemo(
		() => buildModel(formConfig, responses),
		[formConfig, responses],
	);

	const score = useMemo(
		() => extractScore(model, responses),
		[model, responses],
	);

	if (error) {
		return (
			<Alert severity="warning" sx={{ m: 2 }}>
				Could not render survey: {error}
			</Alert>
		);
	}

	if (!model) {
		// Fallback when no schema is available — render whatever submitted
		// keys we have so reviewers still see something.
		const data = responses ?? {};
		if (!Object.keys(data).length) {
			return (
				<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
					No form responses submitted.
				</Typography>
			);
		}
		return (
			<Stack spacing={1.5} sx={{ p: 2 }}>
				{Object.entries(data).map(([key, value]) => (
					<Box key={key}>
						<Typography variant="caption" color="text.secondary">
							{key}
						</Typography>
						<Typography variant="body2">
							{value === null || value === undefined
								? "—"
								: typeof value === "object"
									? JSON.stringify(value)
									: String(value)}
						</Typography>
					</Box>
				))}
			</Stack>
		);
	}

	return (
		<Box sx={{ p: 2 }}>
			{typeof score === "number" && (
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="subtitle2">Score:</Typography>
					<Chip
						label={String(score)}
						color={score >= 10 ? "warning" : "default"}
						size="small"
					/>
					{score >= 10 && (
						<Typography variant="caption" color="warning.main">
							Threshold reached — flag for follow-up
						</Typography>
					)}
				</Stack>
			)}
			<Survey model={model} />
		</Box>
	);
};
